import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import Link from 'next/link'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { paths } from '@/constants/paths'
import { useLocalStorage } from '@/hooks/useLocaleStorage/useLocalStorage'

interface Props {
  isStarburst?: boolean
  page: string
  projectSlug: string
}

export function DavidPifCTA({ isStarburst = false, page, projectSlug }: Props) {
  const storageKey = `david-pif-cta-closed-${page}-${projectSlug}`
  const {
    storedValue: isClosed,
    setStoredValue: setIsClosed,
    isReady: isReadyStorage,
  } = useLocalStorage<boolean>(storageKey, false, 'boolean')

  const [isOpen, setIsOpen] = useState(false)

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsClosed(true)
    setIsOpen(false)
  }

  useEffect(() => {
    if (isReadyStorage && !isClosed && !isOpen) {
      setIsOpen(true)
    }
  }, [isClosed, isOpen, isReadyStorage])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '200%' }}
          animate={{
            y: 0,
            transition: {
              duration: 0.6,
              delay: 1.5,
              ease: [0.68, -0.55, 0.265, 1.55],
            },
          }}
          exit={{
            y: '200%',
            transition: {
              duration: 0.6,
              ease: [0.68, -0.55, 0.265, 1.55],
            },
          }}
          className={classNames('fixed bottom-4 right-4 z-50', {
            'md:h-[135px] md:w-[238px] h-[115px] w-[208px]': !isStarburst,
            'md:h-[138px] md:w-[141px] w-[131px] h-[118px] md:bottom-8 md:right-8': isStarburst,
          })}
        >
          <Link
            href={`${paths.payItForward.index}/david`}
            className="block h-full w-full cursor-pointer transition-opacity duration-200 hover:opacity-90"
            aria-label="Share DAVID with others - Pay it Forward"
          >
            {isStarburst ? (
              <Image
                alt="Share DAVID with Others"
                className="object-cover"
                src="/v1763745953/theatrical/david/pif-starburst-graphic.png"
                fill
                sizes="(min-width: 768px) 141px, 131px"
              />
            ) : (
              <Image
                alt="Share DAVID with Others"
                className="object-cover"
                src="/v1761152031/theatrical/david/gift-david-graphic-overlay"
                fill
                sizes="(min-width: 768px) 238px, 208px"
              />
            )}
            <button
              onClick={handleClose}
              className={classNames(
                'absolute z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 p-2 transition-opacity hover:opacity-80',
                {
                  'md:-top-4 md:right-4 -top-5 right-1': !isStarburst,
                  'md:-top-4 -top-5 right-0 md:right-1': isStarburst,
                },
              )}
              aria-label="Close"
            >
              <CloseIcon size={16} color="white" />
            </button>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
